/**
 * Tab Configuración del Panel People: edita PEOPLE_FLOW_CONFIG (motor,
 * representantes/canales/ppt por clapbase, links, validación). Lectura para
 * toda la sección; edición solo People/admin (el server lo gatea con
 * peopleProcedure — acá solo se deshabilita la UI).
 */

import { currentLocale } from '@/lib/locale';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';

interface FlowConfigDraft {
  enabled: boolean;
  replaceManagerMailWithSlack: boolean;
  sources: {
    altasSpreadsheetId?: string;
    altasSheetName?: string;
    hobbySheets: Record<string, { spreadsheetId: string; sheetName: string }>;
  };
  representatives: Record<string, { name: string; email: string }>;
  channels: Record<string, Array<{ id: string; name: string }>>;
  rooms: Record<string, { resourceEmail: string; label: string }>;
  links: {
    checklist: string;
    peopleLanding: string;
    signatureTemplate: string;
    curricula: string;
    week1Form: string;
    checkin30Form: string;
    welcomeVideoUrl: string;
    globalPpt: string;
    whatsappGeneral: string;
    pptByClapbase: Record<string, string>;
    whatsappByClapbase: Record<string, string>;
  };
  globalOnboarding: {
    defaultTime: string;
    hosts?: string[];
    overrides: Record<string, { date: string; time: string; meetLink?: string }>;
  };
  validation: { requiredFields: string[]; renotifyHours: number };
}

/** "#cordoba:C0XX, #almuerzo-cba" ⇄ [{name,id}] */
const channelsToText = (list: Array<{ id: string; name: string }>) =>
  list.map(c => (c.id ? `${c.name}:${c.id}` : c.name)).join(', ');
const textToChannels = (text: string) =>
  text
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(tok => {
      const [name, id] = tok.split(':').map(s => s.trim());
      return { name: name.startsWith('#') ? name : `#${name}`, id: id ?? '' };
    });

const LINK_KEYS = [
  'checklist',
  'peopleLanding',
  'signatureTemplate',
  'curricula',
  'week1Form',
  'checkin30Form',
  'welcomeVideoUrl',
  'globalPpt',
  'whatsappGeneral',
] as const;

export function OnboardingConfigTab() {
  const { t } = useTranslation();
  const { roles, isAdmin } = useAuth();
  const canEdit = isAdmin || (roles ?? []).includes('people');
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.peopleFlow.getFlowConfig.useQuery(undefined, { staleTime: 30_000 });
  const save = trpc.peopleFlow.saveFlowConfig.useMutation({
    onSuccess: () => {
      toast.success(t('users.people_panel.config.saved'));
      utils.peopleFlow.getFlowConfig.invalidate();
      utils.peopleFlow.getFlowStatus.invalidate();
    },
    onError: e => toast.error(e.message),
  });

  const [draft, setDraft] = useState<FlowConfigDraft | null>(null);
  const [channelsText, setChannelsText] = useState<Record<string, string>>({});
  const [newClapbase, setNewClapbase] = useState('');

  useEffect(() => {
    if (data?.config && !draft) {
      const cfg = data.config as FlowConfigDraft;
      setDraft(JSON.parse(JSON.stringify(cfg)));
      setChannelsText(
        Object.fromEntries(Object.entries(cfg.channels ?? {}).map(([k, v]) => [k, channelsToText(v)]))
      );
    }
  }, [data?.config, draft]);

  const clapbases = useMemo(() => {
    if (!draft) return [];
    return Array.from(
      new Set([
        ...Object.keys(draft.representatives ?? {}),
        ...Object.keys(draft.channels ?? {}),
        ...Object.keys(draft.links.pptByClapbase ?? {}),
        ...Object.keys(draft.links.whatsappByClapbase ?? {}),
      ])
    ).sort((a, b) => a.localeCompare(b, 'es'));
  }, [draft]);

  if (isLoading || !draft) return <WinclapLoader />;

  const setRep = (cb: string, field: 'name' | 'email', value: string) =>
    setDraft(d => d && ({
      ...d,
      representatives: { ...d.representatives, [cb]: { ...(d.representatives[cb] ?? { name: '', email: '' }), [field]: value } },
    }));

  const handleSave = () => {
    const channels = Object.fromEntries(
      Object.entries(channelsText).map(([k, v]) => [k, textToChannels(v)])
    );
    save.mutate({ patch: { ...draft, channels } });
  };

  const addClapbase = () => {
    const name = newClapbase.trim();
    if (!name || !draft) return;
    setDraft({ ...draft, representatives: { ...draft.representatives, [name]: { name: '', email: '' } } });
    setChannelsText(prev => ({ ...prev, [name]: '' }));
    setNewClapbase('');
  };

  return (
    <div className="max-w-4xl space-y-4">
      {!canEdit && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('users.people_panel.config.readonly_title')}</AlertTitle>
          <AlertDescription>{t('users.people_panel.config.readonly_desc')}</AlertDescription>
        </Alert>
      )}

      {/* Motor */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">{t('users.people_panel.config.engine_title')}</h3>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">{t('users.people_panel.config.enabled_label')}</p>
            <p className="text-xs text-muted-foreground">{t('users.people_panel.config.enabled_desc')}</p>
          </div>
          <Switch
            checked={draft.enabled}
            disabled={!canEdit}
            onCheckedChange={v => setDraft({ ...draft, enabled: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">{t('users.people_panel.config.leader_slack_label')}</p>
            <p className="text-xs text-muted-foreground">{t('users.people_panel.config.leader_slack_desc')}</p>
          </div>
          <Switch
            checked={draft.replaceManagerMailWithSlack}
            disabled={!canEdit}
            onCheckedChange={v => setDraft({ ...draft, replaceManagerMailWithSlack: v })}
          />
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm">{t('users.people_panel.config.renotify_label')}</p>
          <Input
            type="number"
            min={1}
            className="h-8 w-24"
            disabled={!canEdit}
            value={draft.validation.renotifyHours}
            onChange={e =>
              setDraft({
                ...draft,
                validation: { ...draft.validation, renotifyHours: Number(e.target.value) || 48 },
              })
            }
          />
        </div>
      </Card>

      {/* Onboarding global del mes */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">{t('admin.onboardingconfigtab.onboarding_global')}</h3>
        <div className="flex items-center gap-3">
          <p className="text-sm">{t('admin.onboardingconfigtab.hora_ultimo_jueves_del_mes')}</p>
          <Input
            type="time"
            className="h-8 w-32"
            disabled={!canEdit}
            value={draft.globalOnboarding.defaultTime}
            onChange={e =>
              setDraft({
                ...draft,
                globalOnboarding: { ...draft.globalOnboarding, defaultTime: e.target.value },
              })
            }
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('admin.onboardingconfigtab.quien_lo_hostea_people')}</p>
          <p className="text-xs text-muted-foreground">
            {t('admin.onboardingconfigtab.se_invitan_siempre_al_evento_ademas')}
          </p>
          <Input
            className="h-8"
            // i18n-ok: ejemplo de emails
            placeholder="mariana.vera@winclap.com, otra.persona@winclap.com"
            disabled={!canEdit}
            value={(draft.globalOnboarding.hosts ?? []).join(', ')}
            onChange={e =>
              setDraft({
                ...draft,
                globalOnboarding: {
                  ...draft.globalOnboarding,
                  hosts: e.target.value.split(',').map(v => v.trim()).filter(Boolean),
                },
              })
            }
          />
        </div>
      </Card>

      {/* Por clapbase */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">{t('users.people_panel.config.clapbase_title')}</h3>
        {/* La planilla trae la ciudad; acá las filas van por clapbase de People.
            Sin esta aclaración alguien carga "Bogota" y "Colombia" en paralelo. */}
        <p className="text-xs text-muted-foreground">{t('users.people_panel.config.clapbase_hint')}</p>
        <div className="hidden sm:grid grid-cols-[0.8fr_0.9fr_1.2fr_1.3fr_1.3fr_1.3fr] gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Clapbase</span>
          <span>{t('users.people_panel.config.rep_name')}</span>
          <span>{t('users.people_panel.config.rep_email')}</span>
          <span>{t('users.people_panel.config.channels_label')}</span>
          <span>{t('users.people_panel.config.whatsapp_label')}</span>
          <span>{t('users.people_panel.config.ppt_label', 'PPT día 1')}</span>
        </div>
        {clapbases.map(cb => (
          <div key={cb} className="grid grid-cols-1 sm:grid-cols-[0.8fr_0.9fr_1.2fr_1.3fr_1.3fr_1.3fr] gap-2 items-center">
            <span className="text-sm font-medium">{cb}</span>
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              value={draft.representatives[cb]?.name ?? ''}
              onChange={e => setRep(cb, 'name', e.target.value)}
            />
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              placeholder="email@winclap.com"
              value={draft.representatives[cb]?.email ?? ''}
              onChange={e => setRep(cb, 'email', e.target.value)}
            />
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              // i18n-ok: sintaxis técnica de ejemplo
              placeholder="#canal:C0XXXX, #otro"
              value={channelsText[cb] ?? ''}
              onChange={e => setChannelsText(prev => ({ ...prev, [cb]: e.target.value }))}
            />
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              placeholder="https://chat.whatsapp.com/…"
              value={draft.links.whatsappByClapbase?.[cb] ?? ''}
              onChange={e =>
                setDraft({
                  ...draft,
                  links: {
                    ...draft.links,
                    whatsappByClapbase: { ...draft.links.whatsappByClapbase, [cb]: e.target.value },
                  },
                })
              }
            />
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              placeholder={t('users.people_panel.config.ppt_placeholder', 'Link a la PPT (sino, va el Landing)')}
              value={draft.links.pptByClapbase?.[cb] ?? ''}
              onChange={e =>
                setDraft({
                  ...draft,
                  links: {
                    ...draft.links,
                    pptByClapbase: { ...draft.links.pptByClapbase, [cb]: e.target.value },
                  },
                })
              }
            />
          </div>
        ))}
        {canEdit && (
          <div className="flex items-center gap-2 pt-1">
            <Input
              className="h-8 w-56 text-sm"
              placeholder={t('users.people_panel.config.new_clapbase')}
              value={newClapbase}
              onChange={e => setNewClapbase(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addClapbase()}
            />
            <Button variant="outline" size="sm" className="h-8 gap-1" onClick={addClapbase}>
              <Plus className="h-3.5 w-3.5" />
              {t('users.people_panel.config.add')}
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('users.people_panel.config.channels_hint')}</p>
      </Card>

      {/* Links */}
      <Card className="p-4 space-y-2">
        <h3 className="font-semibold">{t('users.people_panel.config.links_title')}</h3>
        {LINK_KEYS.map(key => (
          <div key={key} className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2 items-center">
            <span className="text-sm">{t(`users.people_panel.config.link_${key}`)}</span>
            <Input
              className="h-8 text-sm"
              disabled={!canEdit}
              value={draft.links[key] ?? ''}
              onChange={e => setDraft({ ...draft, links: { ...draft.links, [key]: e.target.value } })}
            />
          </div>
        ))}
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {data?.updatedBy
            ? t('users.people_panel.config.last_edited', {
                by: data.updatedBy,
                at: data.updatedAt ? new Date(data.updatedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }) : '',
              })
            : t('users.people_panel.config.defaults_note')}
        </p>
        <Button className="gap-1.5" disabled={!canEdit || save.isPending} onClick={handleSave}>
          <Save className="h-4 w-4" />
          {t('users.people_panel.config.save')}
        </Button>
      </div>
    </div>
  );
}
